<template>
  <b-overlay
      :show="isLoading"
      rounded="sm"
      opacity="0.65"
  >
    <section class="delivery-settings">
      <b-card class="settings-header mb-2">
        <h1 class="settings-title">
          Delivery Speed Settings
        </h1>
      </b-card>

      <b-card
          v-if="!isLoading && !speeds.length"
          class="empty-state"
      >
        <div class="empty-state-content">
          <div class="empty-state-icon">
            !
          </div>

          <h3 class="empty-state-title">
            No delivery speeds found
          </h3>

          <p class="empty-state-description">
            There are currently no delivery speed settings to display.
          </p>
        </div>
      </b-card>

      <div
          v-else
          class="speed-grid"
      >
        <b-card
            v-for="speed in speeds"
            :key="speed.id"
            class="speed-card"
            :class="{ 'speed-card-saving': isSaving(speed.id) }"
        >
          <div class="speed-card-header">
            <h3 class="speed-name">
              {{ speed.name }}
            </h3>
          </div>

          <div class="slider-container">
            <div class="slider-top">
              <label
                  :for="`delivery-speed-${speed.id}`"
                  class="slider-label"
              >
                Value
              </label>
            </div>

            <div class="range-wrapper">
              <div
                  class="range-value-bubble"
                  :style="getBubbleStyle(speed.id)"
              >
                {{ percentById[speed.id] }}%
              </div>

              <b-form-input
                  :id="`delivery-speed-${speed.id}`"
                  v-model.number="percentById[speed.id]"
                  :disabled="isSaving(speed.id)"
                  :style="getRangeStyle(speed.id)"
                  class="speed-range"
                  min="0"
                  max="100"
                  step="1"
                  type="range"
              />
            </div>

            <div class="range-labels">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          <b-button
              block
              size="sm"
              variant="primary"
              class="save-button"
              :disabled="isSaving(speed.id)"
              @click="updateSpeed(speed.id)"
          >
            <b-spinner
                v-if="isSaving(speed.id)"
                small
                class="mr-50"
            />

            {{ isSaving(speed.id) ? 'Saving...' : 'Save Changes' }}
          </b-button>
        </b-card>
      </div>
    </section>
  </b-overlay>
</template>

<script>
import {
  BButton,
  BCard,
  BFormInput,
  BOverlay,
  BSpinner,
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  GetAllDeliverySpeeds,
  UpdateDeliverySpeedsValue,
} from '@/libs/Api/deliverySpeed'

export default {
  name: 'DeliverySpeedsSetting',

  components: {
    BButton,
    BCard,
    BFormInput,
    BOverlay,
    BSpinner,
  },

  data() {
    return {
      isLoading: false,
      speeds: [],
      percentById: {},
      savingIds: [],
    }
  },

  created() {
    this.loadSpeeds()
  },

  methods: {
    executeRequest(request) {
      return new Promise((resolve, reject) => {
        request.fetch(resolve, reject)
      })
    },

    async loadSpeeds() {
      this.isLoading = true

      try {
        const request = new GetAllDeliverySpeeds(this)
        const content = await this.executeRequest(request)

        this.speeds = content.data.data

        this.speeds.forEach(speed => {
          this.$set(
              this.percentById,
              speed.id,
              Math.round(speed.value * 100),
          )
        })
      } catch (error) {
        this.showToast(
            'error',
            error?.message || 'Failed to load delivery speeds.',
        )
      } finally {
        this.isLoading = false
      }
    },

    async updateSpeed(id) {
      if (this.isSaving(id)) {
        return
      }

      this.savingIds.push(id)

      try {
        const request = new UpdateDeliverySpeedsValue(this)

        request.setParams(
            {
              id,
              value: this.percentById[id] / 100,
            },
            false,
        )

        const content = await this.executeRequest(request)

        if (!content.isSuccess) {
          throw new Error(
              content.message || 'Update failed.',
          )
        }

        this.showToast(
            'success',
            `Delivery speed updated to ${this.percentById[id]}%.`,
        )
      } catch (error) {
        this.showToast(
            'error',
            error?.message || 'Update failed.',
        )
      } finally {
        this.savingIds = this.savingIds.filter(
            item => item !== id,
        )
      }
    },

    isSaving(id) {
      return this.savingIds.includes(id)
    },

    getRangeStyle(id) {
      return {
        '--range-progress': `${this.percentById[id]}%`,
      }
    },

    getBubbleStyle(id) {
      const percent = this.percentById[id]

      return {
        left: `${percent}%`,
        transform: `translateX(-${percent}%)`,
      }
    },

    showToast(type, text) {
      const success = type === 'success'

      this.$toast({
        component: ToastificationContent,
        position: 'bottom-center',
        props: {
          title: success ? 'Success' : 'Error',
          variant: success ? 'success' : 'danger',
          icon: success ? 'CheckIcon' : 'XIcon',
          text,
        },
      })
    },
  },
}
</script>

<style scoped>
.delivery-settings {
  width: 100%;
  margin: 30px auto 0;
  padding: 0.25rem;
}

.settings-header {
  overflow: hidden;
  border: 1px solid #ebe9f1;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.06);
}

.settings-title {
  margin: 0;
  color: #2c2c2c;
  font-size: 1.6rem;
  font-weight: 700;
}

.speed-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.speed-card {
  height: 100%;
  overflow: visible;
  border: 1px solid #ebe9f1;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.06);
  transition:
      transform 0.2s ease,
      box-shadow 0.2s ease,
      opacity 0.2s ease;
}

.speed-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(34, 41, 47, 0.1);
}

.speed-card-saving {
  opacity: 0.75;
}

.speed-card-header {
  margin-bottom: 1.5rem;
}

.speed-name {
  margin: 0;
  color: #2c2c2c;
  font-size: 1.1rem;
  font-weight: 700;
}

.slider-container {
  margin-bottom: 1.5rem;
  padding: 1rem 1rem 0.9rem;
  border-radius: 12px;
  background: #f8f8fb;
}

.slider-top {
  margin-bottom: 2rem;
}

.slider-label {
  margin: 0;
  color: #5e5873;
  font-size: 0.85rem;
  font-weight: 600;
}

.range-wrapper {
  position: relative;
  width: 100%;
}

.range-value-bubble {
  position: absolute;
  z-index: 2;
  bottom: calc(100% + 8px);
  display: flex;
  min-width: 46px;
  height: 28px;
  padding: 0 0.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  color: #fff;
  background: #7367f0;
  font-size: 0.75rem;
  font-weight: 700;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 3px 8px rgba(115, 103, 240, 0.3);
}

.range-value-bubble::after {
  position: absolute;
  top: 100%;
  left: 50%;
  width: 0;
  height: 0;
  border-top: 5px solid #7367f0;
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
  content: '';
  transform: translateX(-50%);
}

.speed-range {
  width: 100%;
  height: 20px;
  margin: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
}

.speed-range:focus {
  outline: none;
}

.speed-range:disabled {
  cursor: not-allowed;
}

.speed-range::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(
      to right,
      #7367f0 0%,
      #7367f0 var(--range-progress),
      #dedbe9 var(--range-progress),
      #dedbe9 100%
  );
}

.speed-range::-webkit-slider-thumb {
  width: 22px;
  height: 22px;
  margin-top: -7px;
  border: 3px solid #fff;
  border-radius: 50%;
  appearance: none;
  -webkit-appearance: none;
  background: #7367f0;
  cursor: grab;
  box-shadow:
      0 2px 8px rgba(115, 103, 240, 0.45),
      0 0 0 2px rgba(115, 103, 240, 0.12);
}

.speed-range::-moz-range-track {
  width: 100%;
  height: 8px;
  border: 0;
  border-radius: 999px;
  background: #dedbe9;
}

.speed-range::-moz-range-progress {
  height: 8px;
  border-radius: 999px;
  background: #7367f0;
}

.speed-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #7367f0;
  cursor: grab;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.45);
}

.range-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 0.55rem;
  color: #9a97a8;
  font-size: 0.7rem;
}

.save-button {
  min-height: 44px;
  border-radius: 10px;
  font-weight: 600;
}

.empty-state {
  border: 1px dashed #d8d6de;
  border-radius: 16px;
  box-shadow: none;
}

.empty-state-content {
  padding: 3rem 1rem;
  text-align: center;
}

.empty-state-icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  margin-bottom: 1rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
  font-size: 1.3rem;
  font-weight: 700;
}

.empty-state-title {
  margin: 0;
  color: #2c2c2c;
  font-size: 1.1rem;
  font-weight: 700;
}

.empty-state-description {
  margin: 0.5rem 0 0;
  color: #6e6b7b;
  font-size: 0.9rem;
}

@media (max-width: 767px) {
  .delivery-settings {
    padding: 0;
  }

  .settings-title {
    font-size: 1.35rem;
  }

  .speed-grid {
    grid-template-columns: 1fr;
  }
}
</style>